import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlexControlledForm,
  ProfileInformationIcon,
  useDebounceState,
  useResponsive,
} from '@common-fe/common-fe';

import { useProfileInformationFormStore } from '@/modules/user/components/SignUp/ProfileInformation/stores/useProfileInformationFormMode.store';
import { centeredFieldStyles } from '@/modules/user/components/SignUp/signUp.constants';
import { useValidateForm } from '@/utils/hooks';

import BlockWrapper from '../BlockWrapper';

import EditProfileInformationButton from './components/EditProfileInformationButton';
import useProfileInformationFields from './hooks/useProfileInformationFields';
import { ProfileInformationPayload, useStore } from './useProfileInformation.store';

interface ProfileInformationProps {
  isOrphanMode?: boolean;
}
const ProfileInformation: React.FC<ProfileInformationProps> = ({ isOrphanMode }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const state = useStore((state) => state.state);
  const handleSetState = useStore((state) => state.setState);
  const showErrors = useStore((state) => state.showErrors);
  const {
    values: { isFormEditMode, isFormError },
  } = useProfileInformationFormStore();

  const { setValues, values } = useDebounceState<ProfileInformationPayload>();

  // Ref breaks the cyclic dependency: fields -> onNext -> handleValidateForm
  // -> handlePreSubmit -> useValidateForm(fields). onNext calls the latest
  // validation handler through the ref without needing it declared beforehand.
  const handleValidateFormRef = useRef<(() => boolean) | undefined>(undefined);

  const fields = useProfileInformationFields({
    isOrphanMode,
    isDisabledMode: !isFormEditMode,
    isError: isFormError,
    onNext: () => handleValidateFormRef.current?.(),
  });

  const { handlePreSubmit, hasErrors } = useValidateForm(fields);

  const handleValidateForm = useCallback(() => {
    return handlePreSubmit(state);
  }, [handlePreSubmit, state]);

  handleValidateFormRef.current = handleValidateForm;

  useEffect(() => {
    if (values) {
      handleSetState(values);
    }
  }, [handleSetState, values]);

  return (
    <BlockWrapper Icon={ProfileInformationIcon} title={t('Profile Information')}>
      <Box background="canvas" round="container1Round">
        <FlexControlledForm
          fields={fields}
          showError={showErrors || hasErrors}
          editMode
          onChangeValues={setValues}
        >
          {isOrphanMode ? null : (
            <Box style={isMobile ? {} : centeredFieldStyles.style}>
              <Box />
              <EditProfileInformationButton onValidation={handleValidateForm} />
            </Box>
          )}
        </FlexControlledForm>
      </Box>
    </BlockWrapper>
  );
};

export default ProfileInformation;
